import { DATA_SOURCE_TYPES, PARCELS_LAYER_ID } from '@/components/maplibre/consts';
import {
  ColumnMetadata,
  LayerTypeInfo,
  ParcelTilesetDataSourceConfig,
  TilesetDataSourceConfig,
} from '@/components/maplibre/types';
import { LAYER_GEOMETRY_TYPE } from '@/lib/db-constants';
import type { MapLayer } from '@/lib/db-row-types';
import { getLayerStyle } from '@/utils/layer-sources/style-processor';
import { createClient } from '@/utils/supabase/client';

interface TilesetLayerData {
  row_id: number;
  properties: any;
  feature_id: string;
  feature_match: string;
}

/**
 * Fetches raw layer data for tileset processing
 */
async function fetchTilesetLayerData(layerId: string): Promise<TilesetLayerData[]> {
  const supabase = createClient();

  const { data, error } = await supabase
    .rpc('get_raw_layer_data', { layer_id: layerId })
    .select('row_id, properties, feature_id, feature_match');

  if (error) {
    throw new Error(`Failed to fetch layer data: ${error.message}`);
  }

  return data || [];
}

/**
 * Builds feature data map for client-side joins
 */
function buildFeatureDataMap(rawData: TilesetLayerData[]): Record<string, Record<string, unknown>> {
  const featureData: Record<string, Record<string, unknown>> = {};

  rawData.forEach(row => {
    if (row.feature_id && row.properties) {
      featureData[row.feature_id] = row.properties;
    }
  });

  return featureData;
}

/**
 * Normalizes source URL to include mapbox:// prefix if needed
 */
function normalizeSourceUrl(source: string): string {
  return source.startsWith('mapbox://') ? source : `mapbox://${source}`;
}

/**
 * Builds a single generic tileset data source configuration
 */
async function buildStandardTilesetDataSource(
  layerInfo: LayerTypeInfo,
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<TilesetDataSourceConfig | null> {
  if (layerInfo.type !== DATA_SOURCE_TYPES.TILESET || !layerInfo.boundaryInfo) {
    return null;
  }

  const { layer, boundaryInfo, joinProperty = 'id' } = layerInfo;

  try {
    const rawLayerData = await fetchTilesetLayerData(layer.id);
    const featureData = rawLayerData.length > 0 ? buildFeatureDataMap(rawLayerData) : {};
    const mapLayer = mapLayers.find(ml => ml.layer_id === layer.id);
    const columnMetadata = columnMetadataMap.get(layer.id);
    const style = await getLayerStyle(mapLayer, layer, columnMetadata);

    const config: TilesetDataSourceConfig = {
      id: layer.id,
      name: layer.title || 'Untitled Tileset Layer',
      type: DATA_SOURCE_TYPES.TILESET,
      source: normalizeSourceUrl(boundaryInfo.source),
      sourceLayer: boundaryInfo.source_layer,
      joinProperty,
      promoteId: joinProperty,
      geometryTypes: [layer.geometry_type || LAYER_GEOMETRY_TYPE.POLYGON],
      featureData,
      style,
      clickStrategy: 'joined-data',
      visible: true,
      columnMetadata,
    };

    if (Object.keys(featureData).length === 0) {
      console.warn(`No valid feature data found for tileset layer: ${layer.id}`);
      return null;
    }

    return config;
  } catch (error) {
    console.error(`Error building generic tileset data source for layer ${layer.id}:`, error);
    return null;
  }
}

/**
 * Builds a single parcel tileset data source configuration (read-only parcels)
 */
async function buildParcelTilesetDataSource(
  layerInfo: LayerTypeInfo,
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<ParcelTilesetDataSourceConfig> {
  const { layer } = layerInfo;
  const mapLayer = mapLayers.find(ml => ml.layer_id === layer.id);
  const columnMetadata = columnMetadataMap.get(layer.id);
  const style = await getLayerStyle(mapLayer, layer, columnMetadata);

  // The archive is a revision of the parcels layer, addressed like every other PMTiles layer.
  const tileId = layer.current_revision_id || layer.id;

  return {
    id: layer.id,
    name: layer.title || 'Parcels',
    type: DATA_SOURCE_TYPES.TILESET,
    source: `pmtiles://${process.env.NEXT_PUBLIC_PARCELS_TILES_BASE_URL}/${tileId}.pmtiles`,
    sourceLayer: 'parcels',
    promoteId: 'robust_id',
    geometryTypes: [layer.geometry_type || LAYER_GEOMETRY_TYPE.POLYGON],
    featureData: {},
    style,
    visible: true,
    clickStrategy: 'fetch-async',
    clickFetcher: (robust_id: string) => {
      return () => fetch(`/api/parcels/${robust_id}`);
    },
  };
}

/**
 * Builds a single tileset data source, delegating to the appropriate builder
 */
export async function buildTilesetDataSource(
  layerInfo: LayerTypeInfo,
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<TilesetDataSourceConfig | ParcelTilesetDataSourceConfig | null> {
  const { layer } = layerInfo;

  if (layer.id === PARCELS_LAYER_ID) {
    return buildParcelTilesetDataSource(layerInfo, mapLayers, columnMetadataMap);
  }

  return buildStandardTilesetDataSource(layerInfo, mapLayers, columnMetadataMap);
}

/**
 * Builds multiple tileset data sources in parallel
 */
export async function buildTilesetDataSources(
  tilesetLayerInfos: LayerTypeInfo[],
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<TilesetDataSourceConfig[]> {
  const buildPromises = tilesetLayerInfos.map(layerInfo =>
    buildTilesetDataSource(layerInfo, mapLayers, columnMetadataMap)
  );

  const results = await Promise.all(buildPromises);

  return results.filter((config): config is TilesetDataSourceConfig => config !== null);
}
